import expenseSetting from "@/services/api/ExpenseSetting";
import { handleResponse } from "@/services/axios";
import {
    createAsyncThunk,
    createSlice,
    isFulfilled,
    isRejected,
    isPending,
} from "@reduxjs/toolkit"

interface LoadingState {
    getApproverLoading: boolean;
}

interface ExpenseState {
    category: any;
    categoryRole: any[];
    categorySetting: any;
    companyMembers: any[];
    defaultFinanceApprovers: any[];
    defaultApprovers: any[];
    approvers: any,
    isFetching: boolean;
    loading: LoadingState;
    error: string | null;
}

const initialState: ExpenseState = {
    category: null,
    categoryRole: [],
    categorySetting: null,
    companyMembers: [],
    defaultFinanceApprovers: [],
    defaultApprovers: [],
    approvers: null,
    isFetching: false,
    loading: {
        getApproverLoading: false,
    },
    error: null,
}

export const addCategory = createAsyncThunk(
    "expenseSetting/AddCategory",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.addCategory(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to add category");
        }
    }
)

export const getCategory = createAsyncThunk(
    "expenseSetting/category",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getCategory(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to get category");
        }
    }
)

export const getCategoryRole = createAsyncThunk(
    "expenseSetting/categoryRole",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getCategoryRole(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to get category");
        }
    }
)

export const getCategorySetting = createAsyncThunk(
    "expenseSetting/categorySetting",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getCategorySetting(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to get category");
        }
    }
)

export const updateCategory = createAsyncThunk(
    "expense/updateCategory",
    async ({ id, payload }: { id: number; payload: any }, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.updateCategory(id, payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to update category");
        }
    }
)

export const deleteCategory = createAsyncThunk(
    "expense/updateCategory",
    async (id: number, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.deleteCategory(id);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to delete category");
        }
    }
)

export const getCompanyMembers = createAsyncThunk(
    "expense/updateCategory",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getCompanyMembers(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch company members");
        }
    }
)

export const setDefaultFinanceApprovers = createAsyncThunk(
    "expenseSetting/setDefaultFinanceApprovers",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.setDefaultFinanceApprovers(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to set default finance approver");
        }
    }
)

export const getDefaultFinanceApprovers = createAsyncThunk(
    "expenseSetting/getDefaultFinanceApprovers",
    async (_, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getDefaultFinanceApprovers();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch default finance approver");
        }
    }
)

export const deleteFinanceApprover = createAsyncThunk(
    "expenseSetting/deleteFinanceApprovers",
    async (approverId: number, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.deleteFinanceApprover(approverId);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to delete default finance approver");
        }
    }
)

export const getDefaultApprovers = createAsyncThunk(
    "expenseSetting/defaultApprovers",
    async (_, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getDefaultApprovers();
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Default Approvers");
        }
    }
)

export const updateFixedTravelCategory = createAsyncThunk(
    "expenseSetting/updateFixedTravelCategory",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.updateFixedTravelCategory(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to update Fixed Travel Category");
        }
    }
)

export const addDefaultApprover = createAsyncThunk(
    "expenseSetting/addDefaultApprover",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.addDefaultApprover(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to update Fixed Travel Category");
        }
    }
)

export const deleteDefaultApprover = createAsyncThunk(
    "expenseSetting/deleteDefaultApprover",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.deleteDefaultApprover(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to delete Default Category");
        }
    }
)

export const getApprovers = createAsyncThunk(
    "expense/getApprovers",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.getApprovers(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Approvers");
        }
    }
)

export const setExpenseApprover = createAsyncThunk(
    "expense/setExpenseApprover",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.setExpenseApprover(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to set Approvers");
        }
    }
)

export const deleteApprover = createAsyncThunk(
    "expense/deleteApprover",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await expenseSetting.deleteApprover(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to delete Approvers");
        }
    }
)

const expenseSlice = createSlice({
    name: "expenseSetting",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCategory.fulfilled, (state, action) => {
                state.category = action.payload || null;
            })
            .addCase(getCategoryRole.fulfilled, (state, action) => {
                state.categoryRole = action.payload || [];
            })
            .addCase(getCategorySetting.fulfilled, (state, action) => {
                state.categorySetting = action.payload || null;
            })
            .addCase(getCompanyMembers.fulfilled, (state, action) => {
                state.companyMembers = action.payload || [];
            })
            .addCase(getDefaultFinanceApprovers.fulfilled, (state, action) => {
                state.defaultFinanceApprovers = action.payload || [];
            })
            .addCase(getDefaultApprovers.fulfilled, (state, action) => {
                state.defaultApprovers = action.payload || [];
            })
            .addCase(getApprovers.pending, (state) => {
                state.loading.getApproverLoading = true;
                state.approvers = null;
                state.error = null;
            })
            .addCase(getApprovers.fulfilled, (state, action) => {
                state.loading.getApproverLoading = false;
                state.approvers = action.payload || null;
            })
            .addCase(getApprovers.rejected, (state, action) => {
                state.loading.getApproverLoading = false;
                state.error = action.payload as string || "Failed to fetch Approvers";
            })
        builder
            .addMatcher(isPending, (state) => {
                state.isFetching = true;
                state.error = null;
            })
            .addMatcher(isFulfilled, (state) => {
                state.isFetching = false;
            })
            .addMatcher(isRejected, (state, action: any) => {
                state.isFetching = false;
                state.error = action.payload || "Something went wrong";
            });
    }
})
export default expenseSlice.reducer;